import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Clock, Instagram, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  CONTACT_EMAIL,
  HOURS,
  INSTAGRAM_URL,
  LOCATION,
} from "@/components/landing/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  email: "",
  subject: "",
  details: "",
};

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [pkg, setPkg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem("jt_selected_package");
      if (saved) {
        setPkg(saved);
        setForm((f) => ({
          ...f,
          subject: f.subject || `Interested in the ${saved} package`,
        }));
      }
    } catch {
      /* ignore */
    }
  }, []);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.email.trim()) e.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email";
    if (!form.subject.trim()) e.subject = "Add a short subject";
    if (!form.details.trim() || form.details.trim().length < 10)
      e.details = "Tell us a bit more (min 10 chars)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        details: form.details.trim(),
        package: pkg || null,
      });
      toast.success("Inquiry sent! Julian will reach out within 24 hours.");
      setForm(INITIAL);
      setPkg("");
      try {
        sessionStorage.removeItem("jt_selected_package");
      } catch {
        /* ignore */
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Couldn’t send your inquiry. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn’t send your inquiry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14">
        {/* Left: Info */}
        <div className="lg:col-span-5">
          <span className="inline-block text-xs uppercase tracking-[0.22em] font-semibold text-orange-600">
            Contact
          </span>
          <h2
            data-testid="contact-heading"
            className="font-display mt-3 text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05]"
          >
            Tell us about your{" "}
            <span className="brand-text-gradient">business.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 font-body max-w-md">
            Get a free quote, ask a question, or book a quick call. We reply
            within 24 hours on business days.
          </p>

          <ul className="mt-10 space-y-3">
            <li>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="contact-email-link"
                className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3.5 hover:border-slate-300 hover:bg-slate-50 transition-colors"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
                  <Mail size={18} />
                </span>
                <span className="flex-1 leading-tight">
                  <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                    Email
                  </span>
                  <span className="block font-display font-extrabold text-slate-900 group-hover:text-blue-700 transition-colors">
                    {CONTACT_EMAIL}
                  </span>
                </span>
              </a>
            </li>

            <li>
              <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3.5">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700 ring-1 ring-slate-200">
                  <MapPin size={18} />
                </span>
                <span className="flex-1 leading-tight">
                  <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                    Location
                  </span>
                  <span
                    data-testid="contact-location"
                    className="block font-display font-extrabold text-slate-900"
                  >
                    {LOCATION}
                  </span>
                </span>
              </div>
            </li>

            <li>
              <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3.5">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700 ring-1 ring-slate-200">
                  <Clock size={18} />
                </span>
                <span className="flex-1 leading-tight">
                  <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                    Open hours
                  </span>
                  <span
                    data-testid="contact-hours"
                    className="block font-display font-extrabold text-slate-900"
                  >
                    {HOURS}
                  </span>
                </span>
              </div>
            </li>

            <li>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-link"
                className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3.5 hover:border-slate-300 hover:bg-slate-50 transition-colors"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
                  <Instagram size={18} />
                </span>
                <span className="flex-1 leading-tight">
                  <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                    Instagram
                  </span>
                  <span className="block font-display font-extrabold text-slate-900 group-hover:text-blue-700 transition-colors">
                    @jtbrandscalesky
                  </span>
                </span>
              </a>
            </li>
          </ul>
        </div>

        {/* Right: Form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            noValidate
            className="rounded-3xl border border-slate-200 bg-white p-6 md:p-9 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.25)]"
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">
                  Get a free quote
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Fields marked are required.
                </p>
              </div>
              {pkg && (
                <span
                  data-testid="contact-selected-package"
                  className="inline-flex items-center gap-2 rounded-full bg-orange-100 text-orange-800 text-xs font-bold px-3 py-1.5"
                >
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-orange-500" />
                  {pkg} package
                </span>
              )}
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              <Field
                id="name"
                label="Your name"
                error={errors.name}
                testid="contact-input-name"
              >
                <Input
                  id="name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Doe"
                  className="h-11 rounded-xl"
                  data-testid="contact-input-name"
                />
              </Field>
              <Field
                id="email"
                label="Email"
                error={errors.email}
                testid="contact-input-email"
              >
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@business.com"
                  className="h-11 rounded-xl"
                  data-testid="contact-input-email"
                />
              </Field>
              <div className="sm:col-span-2">
                <Field
                  id="subject"
                  label="Subject"
                  error={errors.subject}
                  testid="contact-input-subject"
                >
                  <Input
                    id="subject"
                    value={form.subject}
                    onChange={update("subject")}
                    placeholder="Looking to grow my restaurant in Lexington"
                    className="h-11 rounded-xl"
                    data-testid="contact-input-subject"
                  />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field
                  id="details"
                  label="Tell us about your business"
                  error={errors.details}
                  testid="contact-input-details"
                >
                  <Textarea
                    id="details"
                    value={form.details}
                    onChange={update("details")}
                    placeholder="What you do, where you are, and what success looks like for you…"
                    rows={5}
                    className="rounded-xl resize-none"
                    data-testid="contact-input-details"
                  />
                </Field>
              </div>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-xs text-slate-500">
                By submitting, you agree to be contacted about your inquiry.
              </p>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="rounded-full bg-orange-600 hover:bg-orange-700 text-white font-semibold h-12 px-7 btn-shine disabled:opacity-70"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Send inquiry <Send size={16} className="ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, children, error, testid }) {
  return (
    <div>
      <Label
        htmlFor={id}
        className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-700"
      >
        {label}
      </Label>
      <div className="mt-2">{children}</div>
      {error && (
        <p
          data-testid={`${testid}-error`}
          className="mt-1.5 text-xs font-medium text-red-600"
        >
          {error}
        </p>
      )}
    </div>
  );
}
